import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useWebSocket, NotificationChannel } from '../contexts/WebSocketContext';
import { ScanProgressBanner } from '../components/NotificationCenter';
import {
  Search, Play, RefreshCw, Cloud, Shield, CheckCircle2,
  AlertTriangle, FileText, Clock, X
} from 'lucide-react';
import { scansAPI, accountsAPI, tenantsAPI, checksAPI, providerIcons, frameworkShortNames } from '../services/amidala';

const AmidalaScan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/amidala') ? '/amidala' : '/grc/amidala';
  const [searchParams] = useSearchParams();
  const preselectedAccount = searchParams.get('account');
  const { subscribe, addListener } = useWebSocket();
  
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [currentScanId, setCurrentScanId] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [checks, setChecks] = useState([]);
  const [error, setError] = useState(null);
  
  const [scanConfig, setScanConfig] = useState({
    name: '',
    description: '',
    selectedAccounts: preselectedAccount ? [preselectedAccount] : [],
    selectedCategories: [],
    complianceFrameworks: []
  });

  // Subscribe to Amidala channel for scan updates
  useEffect(() => {
    subscribe(NotificationChannel.AMIDALA);
    
    // Listen for scan completion
    const cleanup = addListener('scan_completed', (data) => {
      if (data.scan_id === currentScanId) {
        setScanning(false);
        navigate(`${basePath}/scans/${currentScanId}`);
      }
    });
    
    return cleanup;
  }, [subscribe, addListener, currentScanId, navigate]);

  const categories = [
    { id: 'iam', name: 'Identity & Access', icon: '🔐', description: 'IAM users, roles, MFA' },
    { id: 'network', name: 'Network Security', icon: '🌐', description: 'Security groups, firewalls' },
    { id: 'storage', name: 'Storage', icon: '💾', description: 'S3, Blob, GCS buckets' },
    { id: 'compute', name: 'Compute', icon: '💻', description: 'EC2, VMs, instances' },
    { id: 'logging', name: 'Logging & Monitoring', icon: '📝', description: 'CloudTrail, logs' },
    { id: 'encryption', name: 'Encryption', icon: '🔒', description: 'KMS, TDE, CMK' },
    { id: 'database', name: 'Database', icon: '🗄️', description: 'RDS, SQL, Cloud SQL' },
  ];

  const frameworks = [
    { id: 'cis_aws', name: 'CIS AWS', description: 'CIS AWS Foundations' },
    { id: 'cis_azure', name: 'CIS Azure', description: 'CIS Azure Foundations' },
    { id: 'cis_gcp', name: 'CIS GCP', description: 'CIS GCP Foundations' },
    { id: 'nist_800_53', name: 'NIST 800-53', description: 'NIST Security Controls' },
    { id: 'pci_dss', name: 'PCI DSS', description: 'Payment Card Security' },
    { id: 'soc2', name: 'SOC 2', description: 'Trust Services Criteria' },
    { id: 'gdpr', name: 'GDPR', description: 'Data Protection' },
    { id: 'hipaa', name: 'HIPAA', description: 'Healthcare Privacy' },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const tenantsRes = await tenantsAPI.list();
      if (tenantsRes.data.length === 0) {
        navigate(basePath);
        return;
      }
      
      const currentTenant = tenantsRes.data[0];
      setTenant(currentTenant);
      
      const accountsRes = await accountsAPI.list(currentTenant.id);
      setAccounts(accountsRes.data);
      
      // Auto-select all accounts if none preselected
      if (!preselectedAccount && accountsRes.data.length > 0) {
        setScanConfig(prev => ({
          ...prev,
          selectedAccounts: accountsRes.data.map(a => a.id)
        }));
      }
      
      const checksRes = await checksAPI.list();
      setChecks(checksRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const toggleAccount = (accountId) => {
    setScanConfig(prev => ({
      ...prev,
      selectedAccounts: prev.selectedAccounts.includes(accountId)
        ? prev.selectedAccounts.filter(id => id !== accountId)
        : [...prev.selectedAccounts, accountId]
    }));
  };

  const toggleCategory = (categoryId) => {
    setScanConfig(prev => ({
      ...prev,
      selectedCategories: prev.selectedCategories.includes(categoryId)
        ? prev.selectedCategories.filter(id => id !== categoryId)
        : [...prev.selectedCategories, categoryId]
    }));
  };

  const toggleFramework = (frameworkId) => {
    setScanConfig(prev => ({
      ...prev,
      complianceFrameworks: prev.complianceFrameworks.includes(frameworkId)
        ? prev.complianceFrameworks.filter(id => id !== frameworkId)
        : [...prev.complianceFrameworks, frameworkId]
    }));
  };

  const handleStartScan = async () => {
    if (!tenant || scanConfig.selectedAccounts.length === 0) return;
    
    try {
      setScanning(true);
      setError(null);
      setScanProgress({ status: 'starting', total: 0, done: 0, elapsedMs: 0 });
      
      const scanData = {
        name: scanConfig.name || `Security Scan - ${new Date().toLocaleDateString()}`,
        description: scanConfig.description || 'Cloud security posture assessment',
        target: {
          account_ids: scanConfig.selectedAccounts,
          categories: scanConfig.selectedCategories,
          check_ids: []
        },
        compliance_frameworks: scanConfig.complianceFrameworks
      };
      
      const response = await scansAPI.create(tenant.id, scanData);
      setCurrentScanId(response.data.id);
      pollScanProgress(response.data.id);
    } catch (err) {
      setError('Failed to start scan');
      setScanning(false);
      setScanProgress(null);
    }
  };

  // Live progress polling — hits GET /amidala/scans/{id} every 800ms
  // and computes % complete from summary.total_checks / expected total.
  // Navigates to the detail page once the scan is complete or failed.
  const pollScanProgress = async (scanId) => {
    const started = Date.now();
    const expectedTotal = checkCount * (scanConfig.selectedAccounts?.length || 1);
    const tick = async () => {
      try {
        const res = await scansAPI.get(scanId);
        const s = res.data || {};
        const done = (s.summary?.total_checks) || 0;
        setScanProgress({
          status: s.status,
          total: expectedTotal,
          done,
          elapsedMs: Date.now() - started,
          summary: s.summary || null,
        });
        if (s.status === 'completed' || s.status === 'failed') {
          setTimeout(() => navigate(`${basePath}/scans/${scanId}`), 900);
          return;
        }
      } catch (_e) { /* keep polling — transient errors are OK */ }
      setTimeout(tick, 800);
    };
    tick();
  };

  const getSelectedAccountsInfo = () => {
    const selected = accounts.filter(a => scanConfig.selectedAccounts.includes(a.id));
    const providers = [...new Set(selected.map(a => a.provider))];
    return { count: selected.length, providers };
  };

  const getCheckCount = () => {
    let filtered = checks;
    
    // Filter by selected providers
    const selectedProviders = accounts
      .filter(a => scanConfig.selectedAccounts.includes(a.id))
      .map(a => a.provider);
    
    if (selectedProviders.length > 0) {
      // Prowler rules can be tagged "multi" / "all" — always keep those alongside a matching provider
      filtered = filtered.filter(c => (
        selectedProviders.includes(c.provider) || ['multi', 'all'].includes(c.provider)
      ));
    }
    
    // Filter by selected categories
    if (scanConfig.selectedCategories.length > 0) {
      filtered = filtered.filter(c => scanConfig.selectedCategories.includes(c.category));
    }
    
    return filtered.length;
  };

  const getCheckBreakdown = () => {
    // Same filtering as getCheckCount but returns per-source counts for the
    // Scan Summary panel (Amidala native vs Prowler / upstream misconfig rules).
    let filtered = checks;
    const selectedProviders = accounts
      .filter(a => scanConfig.selectedAccounts.includes(a.id))
      .map(a => a.provider);
    if (selectedProviders.length > 0) {
      filtered = filtered.filter(c => (
        selectedProviders.includes(c.provider) || ['multi', 'all'].includes(c.provider)
      ));
    }
    if (scanConfig.selectedCategories.length > 0) {
      filtered = filtered.filter(c => scanConfig.selectedCategories.includes(c.category));
    }
    const native = filtered.filter(c => c.source === 'amidala_native' || !c.source).length;
    const prowler = filtered.filter(c => c.source === 'prowler').length;
    const vulnerabilities = filtered.filter(c => c.source === 'vulnerability_db').length;
    const other = filtered.length - native - prowler - vulnerabilities;
    return { native, prowler, vulnerabilities, other, total: filtered.length };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-12 text-center">
        <Cloud className="w-20 h-20 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">No Cloud Accounts</h2>
        <p className="text-gray-600 mb-6">
          Add a cloud account before running a scan.
        </p>
        <button
          onClick={() => navigate(`${basePath}/accounts`)}
          className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Add Cloud Account
        </button>
      </div>
    );
  }

  const { count: selectedCount, providers: selectedProviders } = getSelectedAccountsInfo();
  const checkCount = getCheckCount();
  const checkBreakdown = getCheckBreakdown();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Search className="w-8 h-8 text-indigo-600" />
            New Security Scan
          </h1>
          <p className="text-gray-600 mt-1">Configure and run a cloud security assessment</p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration Panel */}
        <div className="lg:col-span-2 space-y-6">
          {/* Scan Name */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan Details</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scan Name
                </label>
                <input
                  type="text"
                  value={scanConfig.name}
                  onChange={(e) => setScanConfig(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={`Security Scan - ${new Date().toLocaleDateString()}`}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={scanConfig.description}
                  onChange={(e) => setScanConfig(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the purpose of this scan..."
                  rows={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Account Selection */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Cloud className="w-5 h-5 text-indigo-600" />
              Cloud Accounts
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {accounts.map((account) => (
                <button
                  key={account.id}
                  onClick={() => toggleAccount(account.id)}
                  className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all text-left ${
                    scanConfig.selectedAccounts.includes(account.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{providerIcons[account.provider]}</span>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{account.name}</p>
                    <p className="text-xs text-gray-500 uppercase">{account.provider}</p>
                  </div>
                  {scanConfig.selectedAccounts.includes(account.id) && (
                    <CheckCircle2 className="w-5 h-5 text-indigo-600" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Category Selection */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              Security Categories
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Select specific categories to scan, or leave empty to scan all.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    scanConfig.selectedCategories.includes(cat.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{cat.icon}</span>
                  <p className="text-sm font-medium text-gray-900 mt-1">{cat.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Compliance Frameworks */}
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-600" />
              Compliance Frameworks
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Generate compliance reports against these frameworks.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {frameworks.map((fw) => (
                <button
                  key={fw.id}
                  onClick={() => toggleFramework(fw.id)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    scanConfig.complianceFrameworks.includes(fw.id)
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-medium text-gray-900">{fw.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{fw.description}</p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Summary Panel */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow p-6 sticky top-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan Summary</h3>
            
            <div className="space-y-4">
              {/* Accounts */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Accounts</span>
                <span className="font-medium text-gray-900">
                  {selectedCount} selected
                </span>
              </div>
              
              {/* Providers */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Providers</span>
                <div className="flex items-center gap-1">
                  {selectedProviders.map(p => (
                    <span key={p} className="text-xl">{providerIcons[p]}</span>
                  ))}
                </div>
              </div>
              
              {/* Checks */}
              <div className="py-3 border-b border-gray-100" data-testid="scan-summary-checks">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Security Checks</span>
                  <span className="font-medium text-gray-900" data-testid="scan-summary-checks-total">
                    {checkCount} checks
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700"
                    data-testid="scan-summary-checks-native"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500"></span>
                    Amidala native: {checkBreakdown.native}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-50 text-orange-700"
                    data-testid="scan-summary-checks-prowler"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>
                    Prowler: {checkBreakdown.prowler}
                  </span>
                  {checkBreakdown.vulnerabilities > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700"
                      data-testid="scan-summary-checks-vulnerabilities"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                      Vulnerabilities: {checkBreakdown.vulnerabilities}
                    </span>
                  )}
                  {checkBreakdown.other > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-700"
                      data-testid="scan-summary-checks-other"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-500"></span>
                      Custom / other: {checkBreakdown.other}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Categories */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Categories</span>
                <span className="font-medium text-gray-900">
                  {scanConfig.selectedCategories.length === 0 
                    ? 'All categories' 
                    : `${scanConfig.selectedCategories.length} selected`}
                </span>
              </div>
              
              {/* Frameworks */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <span className="text-gray-600">Compliance</span>
                <span className="font-medium text-gray-900">
                  {scanConfig.complianceFrameworks.length === 0 
                    ? 'None selected' 
                    : `${scanConfig.complianceFrameworks.length} frameworks`}
                </span>
              </div>
              
              {/* Estimated Time — recalibrated Feb 2026:
                  demo accounts scan the simulator at ~50 checks/sec, real
                  AWS accounts hit STS + service APIs at ~12 checks/sec.
                  Weighted average across the selected mix. */}
              <div className="flex items-center justify-between py-3">
                <span className="text-gray-600 flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Est. Duration
                </span>
                <span className="font-medium text-gray-900" data-testid="scan-est-duration">
                  {(() => {
                    const selected = accounts.filter(a => scanConfig.selectedAccounts.includes(a.id));
                    if (selected.length === 0 || checkCount === 0) return '~0 seconds';
                    const demoCount = selected.filter(a => a.is_demo).length;
                    const realCount = selected.length - demoCount;
                    const demoSec = (checkCount * demoCount) / 50;
                    const realSec = (checkCount * realCount) / 12;
                    const totalSec = Math.max(2, Math.ceil(demoSec + realSec));
                    if (totalSec < 60) return `~${totalSec} seconds`;
                    const mins = Math.floor(totalSec / 60);
                    const secs = totalSec % 60;
                    return `~${mins}m ${secs}s`;
                  })()}
                </span>
              </div>
            </div>

            {/* Start Button */}
            <button
              onClick={handleStartScan}
              disabled={scanning || selectedCount === 0}
              className={`w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                scanning || selectedCount === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {scanning ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  {scanProgress?.status === 'completed' ? 'Scan complete — opening…'
                    : scanProgress?.status === 'failed' ? 'Scan failed — see details'
                    : scanProgress?.done > 0 ? 'Scanning…' : 'Starting Scan…'}
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Start Security Scan
                </>
              )}
            </button>

            {/* Live progress bar — visible from scan-start until we
                navigate to the detail page. */}
            {scanning && scanProgress && (
              <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-3"
                   data-testid="scan-progress-panel">
                <div className="flex items-center justify-between text-xs font-medium text-indigo-900">
                  <span data-testid="scan-progress-status">
                    {scanProgress.status === 'completed' ? '✓ Completed'
                      : scanProgress.status === 'failed' ? '⚠ Failed'
                      : scanProgress.status === 'running' ? '⏱ Running'
                      : '· Queued'}
                  </span>
                  <span data-testid="scan-progress-elapsed">
                    {Math.floor((scanProgress.elapsedMs || 0) / 1000)}s elapsed
                  </span>
                </div>
                {(() => {
                  const pct = scanProgress.total > 0
                    ? Math.min(100, Math.floor((scanProgress.done / scanProgress.total) * 100))
                    : (scanProgress.status === 'completed' ? 100 : 8);
                  return (
                    <div className="mt-2">
                      <div className="h-2 rounded-full bg-indigo-100 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            scanProgress.status === 'failed'
                              ? 'bg-red-500'
                              : scanProgress.status === 'completed'
                                ? 'bg-emerald-500'
                                : 'bg-indigo-600'
                          }`}
                          style={{ width: `${pct}%` }}
                          data-testid="scan-progress-bar"
                        />
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-indigo-900/70 mt-1">
                        <span data-testid="scan-progress-checks">
                          {scanProgress.done} / {scanProgress.total || '…'} checks
                        </span>
                        <span>{pct}%</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {selectedCount === 0 && (
              <p className="text-center text-sm text-red-600 mt-2">
                Select at least one account
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AmidalaScan;
